import React, { useEffect, useState } from "react";
import { FaCalculator } from "react-icons/fa";
import { FaList } from "react-icons/fa6";
import CardHeading from "../../../shared/CardHeading";
import { fetchData } from "../../../helpers/fetchData";
import TextInput from "../../../shared/TextInput";
import DropdownField from "../../../shared/DropdownField";
import { DeleteIcon } from "../../../utils/TableActionIcons";
import SwitchInput from "../../../shared/SwitchInput";
import {
  approvalStatusOptions,
  getGroupByTabs,
  searchedPriceItemsOptionsFormat,
  visitDiscountOptions,
} from "../../../constants/VisitsConstants";
import SearchPriceListField from "../../../shared/SearchPriceListField";
import CostManagementModal from "../../../shared/CostManagementModal";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { getDepartmentDiscountLimit, isBusiness42Receptionist } from "../../../utils/businessLevelAccess";
import { getGroupItemFirst, groupByItemType } from "../../../utils/GroupByItem";
import CustomTabs from "../../../shared/CustomTabs";
import TriStateCheckbox from "../../../shared/TriStateCheckbox";
import {
  getBillTabKey,
  getUnitSelectionState,
  isApprovalPendingItem,
} from "../../../utils/payBill";
import { CalculateAgeInYears } from "../../../utils/CalculateAge";
import CheckPermission from "../../../hooks/CheckPermission";
import {
  getItemStockCode,
  getOutOfStockMessage,
  isOutOfStockItem,
  isQuantityOverStock,
  showStockLimitToast,
} from "../../../utils/StockAlerts";

const VisitPriceItemsPayBill = ({
  selectedPayerData,
  setBillingVisitData,
  selectedPatientData,
  isAddCheckBox = false,
  isAlowActions = true,
  isShowTitle = true,
  billingVisitData,
  activeIndex,
  priceListType,
  department_id,
  itemsLoading,
  isGroupedTable = false,
  getGroupTotal,
}) => {
  const { values, setFieldValue } = useFormikContext();

  const [searchedItem, setSearchedItem] = useState("");
  const [itemLoading, setItemLoading] = useState(false);
  const [itemsSuggestions, setItemsSuggestions] = useState([]);
  const { cashPayers } = useSelector((state) => state?.visits);
  const cashPayerId = cashPayers?.[0]?.payer_id;
  const { t, i18n } = useTranslation();
  const isAdmin = useSelector(
    (state) => state.auth.user?.role?.name_en == "Admin",
  );
  const isManager = useSelector(
    (state) => state.auth.user?.role?.name_en == "manager",
  );
  const isReceptionist = useSelector(
    (state) => state.auth.user?.role?.name_en == "Receptionist",
  );
  const isAccountant = useSelector(
    (state) => state.auth.user?.role?.name_en == "Accountant",
  );

  const handleSearchItems = async (event) => {
    const query = event?.query?.trim();
    setItemLoading(true);

    try {
      if (!query || query.length < 2) return setItemsSuggestions([]);

      if (!values?.payerName) {
        toast.error(
          "Payer selection is required before proceeding with items.",
        );
        return;
      }

      const payerId = values?.payerName;
      const isInsurance =
        selectedPatientData?.beneficiary_type != "cash" &&
        payerId !== cashPayerId;
      const basePayload = {
        search: event?.query,
        department_id: department_id,
        age: CalculateAgeInYears(selectedPatientData?.dob),
        gender: selectedPatientData?.gender || "other",
      };

      const requestData = async (targetPayerId, type = "insurance") => {
        try {
          const mPriceRes = await fetchData(
            "POST",
            `/v3/payer/checkMasterPrice`,
            {
              payer_id: targetPayerId,
            },
          );
          const isMasterPrice = mPriceRes?.is_master_price || false;

          const response = await fetchData("POST", "/v3/pricelists/search", {
            ...basePayload,
            payer_id: targetPayerId,
            item_type: type,
            is_master_price: isMasterPrice,
          });

          const formatedData = response?.data
            ? response.data.map((data) => ({
              ...data,
              is_master_price: isMasterPrice,
              payer_vat: isMasterPrice
                ? mPriceRes?.is_vat_applicateable === true
                : data?.payer_details?.[0]?.payer?.is_vat_applicable === true,
            }))
            : [];
          return { success: true, data: formatedData };
        } catch (error) {
          console.error(
            `Error fetching data for payer ${targetPayerId}:`,
            error,
          );
          return { success: false, data: [] };
        }
      };

      try {
        let primaryRes, secondaryRes;

        if (isInsurance) {
          primaryRes = await requestData(payerId, "insurance");
          secondaryRes = await requestData(cashPayerId || payerId, "cash");
        } else {
          primaryRes = await requestData(payerId, "cash");
          secondaryRes = null;
        }

        const primaryOptions = primaryRes?.success
          ? searchedPriceItemsOptionsFormat(primaryRes.data, payerId)
          : [];
        const secondaryOptions = secondaryRes?.success
          ? searchedPriceItemsOptionsFormat(
            secondaryRes.data,
            cashPayerId || payerId,
          )
          : [];

        if (isInsurance) {
          const combinedData = [
            {
              label: t("Insurance"),
              options: primaryOptions?.map((pr) => ({
                ...pr,
                fromInsurance: true,
              })),
            },
            {
              label: t("Cash"),
              options: secondaryOptions,
            },
          ].filter(
            (group) => Array.isArray(group.options) && group.options.length > 0,
          );

          setItemsSuggestions(combinedData);
        } else {
          setItemsSuggestions(primaryOptions);
        }
      } catch (error) {
        console.error("Error processing items:", error);
        setItemsSuggestions([]);
      }
    } catch (e) {
      console.error("Unexpected error:", e);
      setItemsSuggestions([]);
    } finally {
      setItemLoading(false);
    }
  };

  const handleSelectSearchedItem = (event) => {
    if (!event?.value?.value) return false;

    let itemData = event?.value?.item;
    const updatedFullItems = (billingVisitData?.items || []).map((itm) => {
      const editedItem = values?.items?.find(
        (vItem) =>
          vItem.item_id === itm.item_id &&
          itm?.status?.toLowerCase() === "unpaid" &&
          vItem.item_category === itm.item_category,
      );
      return editedItem ? { ...itm, ...editedItem } : itm;
    });

    let existingItems = [...updatedFullItems];
    let selectedItemPayer = itemData?.payer_details?.[0];
    const newItemCategory =
      selectedItemPayer?.payer?.category ||
      (event?.value?.fromInsurance ? "insurance" : "cash");

    let existingIndex = existingItems.findIndex(
      (item) =>
        item.item_id === itemData?.id &&
        item?.status?.toLowerCase() === "unpaid" &&
        item?.item_category === newItemCategory,
    );

    //let selectedItemPayer = itemData?.payer_details?.find((data)=>(data?.payer_id==selectedPayerData?.his_payer_id));
    //let isMasterPrice = masterPriceCheck || selectedItemPayer?.payer?.is_master_price;
    let isMasterPrice =
      itemData?.is_master_price || selectedItemPayer?.payer?.is_master_price;
    const payerVat = itemData?.payer_vat === true;
    const selectedServiceDiscount =
      selectedItemPayer?.payer?.service_discount?.find(
        (item) => item?.service_id === itemData?.price_list_type?.id,
      );
    const itemCode = getItemStockCode(itemData, selectedItemPayer);

    if (isOutOfStockItem(itemData)) {
      toast.error(getOutOfStockMessage(itemData, selectedItemPayer));
      setSearchedItem("");
      return null;
    }

    if (existingIndex !== -1) {
      const targetItem = { ...existingItems[existingIndex] };
      const nextQuantity = Number(targetItem.quantity) + 1;
      if (isQuantityOverStock(itemData, nextQuantity)) {
        showStockLimitToast(toast, itemData?.stock);
        setSearchedItem("");
        return null;
      }
      targetItem.quantity = nextQuantity;

      if (selectedItemPayer?.is_approval) {
        targetItem.approved_qty = Number(targetItem.approved_qty) + 1;
      }

      targetItem.total_quantity = (Number(targetItem.total_quantity) || 0) + 1;
      existingItems[existingIndex] = targetItem;
    } else {
      const number_of_sessions = itemData?.session
        ? itemData?.number_of_sessions
        : 0;
      const basePrice = isMasterPrice
        ? itemData?.master_price
        : (selectedItemPayer?.payer_price ?? 0);

      let newItem = {
        item_name: selectedItemPayer?.non_standard_description,
        item_id: itemData?.id,
        is_stock: itemData?.is_stock,
        stock: itemData?.stock,
        number_of_sessions: number_of_sessions,
        original_price: basePrice || 0,
        emr_items: itemData?.emr_items || [],
        manage_costs: itemData?.manage_costs || [],
        internal_pharmacy: itemData?.internal_pharmacy,
        is_consumable: itemData?.is_consumable || false,
        item_type: itemData?.price_list_type?.name_en,
        standard_code: itemData?.standard_code,
        standard_description: itemData?.standard_description,
        master_code: itemData?.non_standard_code,
        supergit_master_code:
          itemData?.source === "sfda" ||
            itemData?.source === "provider_pharmacy"
            ? itemData?.supergit_master_code || itemData?.non_standard_code
            : itemData?.supergit_master_code || null,
        payer_vat: payerVat,
        non_standard_code:
          itemCode ||
          selectedItemPayer?.agreed_code ||
          selectedItemPayer?.non_standard_code ||
          "",
        non_standard_description:
          itemData?.non_standard_description ||
          selectedItemPayer?.agreed_description_en ||
          selectedItemPayer?.non_standard_description ||
          "",
        non_standard_description_ar:
          itemData?.non_standard_description_ar ||
          selectedItemPayer?.agreed_description_ar ||
          selectedItemPayer?.non_standard_description_ar ||
          "",
        is_package: "false",
        quantity: 1,
        total_quantity: 1,
        unit_price: (number_of_sessions
          ? basePrice / number_of_sessions
          : basePrice || 0
        ).toFixed(2),
        tax_amount: 0,
        factor: 0,
        net: 0,
        item_category: newItemCategory,
        is_approval: selectedItemPayer?.is_approval || false,
        ...(selectedItemPayer?.is_approval
          ? newItemCategory === "insurance"
            ? {
              approved_qty: 1,
              approved_amount: Number(
                (number_of_sessions
                  ? basePrice / number_of_sessions
                  : basePrice || 0
                ).toFixed(2),
              ),
              approval_status: "pending",
              approval_reason: "Pending due to insurance item requiring approval",
            }
            : {
              approved_qty: 1,
              approved_amount: Number(
                (number_of_sessions
                  ? basePrice / number_of_sessions
                  : basePrice || 0
                ).toFixed(2),
              ),
              approval_status: "approved",
            }
          : {}),
        discount_amount:
          selectedServiceDiscount?.patient_discount ||
          selectedItemPayer?.payer?.generals?.discount ||
          selectedItemPayer?.discount_percentage ||
          selectedItemPayer?.discount_amount ||
          itemData?.discount_percentage ||
          0,
        discount_type: "percentage",
        patient_share:
          selectedItemPayer?.payer?.category == "cash" ||
            !event?.value?.fromInsurance
            ? 100
            : selectedServiceDiscount?.patient_share ||
            selectedPayerData?.patient_share,
        patientShareAmount: 0,
        selected_category: itemData?.price_list_type?.category || "",
        payer_share: "",
        status: "UnPaid",
        fromVisit: true,
        payer_id: selectedItemPayer?.payer_id
          ? selectedItemPayer?.payer_id
          : newItemCategory === "insurance"
            ? values?.payerName
            : cashPayerId,
        days_supply: 0,
        checked: true,
        invoice_no: 0,
        company_invoice_no: 0,
        tax_check:
          selectedPatientData?.nationality?.toLowerCase() === "saudi" ||
            itemData?.apply_vat == false
            ? false
            : true,
        order_changeable_price: itemData?.order_changeable_price,
      };
      existingItems.push(newItem);
    }

    setBillingVisitData((prev) => ({
      ...prev,
      items: existingItems,
    }));
    setSearchedItem("");
  };
  return (
    <div className="col-span-12 my-2">
      {isShowTitle && (
        <CardHeading title={t("Items")} Icon={FaList} iconSize={15} />
      )}
      <div className="w-full mt-2">
        {isAlowActions && (
          <SearchPriceListField
            searchedItem={searchedItem}
            itemsSuggestions={itemsSuggestions}
            setSearchedItem={setSearchedItem}
            loading={itemLoading}
            handleSelectSearchedItem={handleSelectSearchedItem}
            handleSearchItems={handleSearchItems}
          />
        )}
        {isGroupedTable ? (
          <PayBillItemsTable
            isAddCheckBox={isAddCheckBox}
            isAlowActions={isAlowActions}
            isReceptionist={isReceptionist}
            isAdmin={isAdmin}
            isManager={isManager}
            isAccountant={isAccountant}
            setBillingVisitData={setBillingVisitData}
            billingVisitData={billingVisitData}
            itemsLoading={itemsLoading}
            priceListType={priceListType}
            getGroupTotal={getGroupTotal}
            showGroupHeaders
          />
        ) : (
          <CustomTabs
            key={`tabs-${activeIndex}`}
            activeTabIndex={activeIndex}
            setFieldValue={setFieldValue}
            billingVisitData={billingVisitData}
            tabs={getGroupByTabs(
              t,
              billingVisitData,
              isAdmin,
              isManager,
              isReceptionist,
              isAddCheckBox,
              isAlowActions,
              priceListType,
              setBillingVisitData,
              isAccountant,
              itemsLoading,
            )}
          />
        )}
      </div>
    </div>
  );
};

export const PayBillItemsTable = ({
  items,
  isAddCheckBox,
  isAlowActions,
  isReceptionist,
  isAdmin,
  isManager,
  setBillingVisitData,
  billingVisitData,
  isAccountant,
  itemsLoading,
  priceListType = [],
  getGroupTotal,
  showGroupHeaders = false,
}) => {
  const { hasPermission } = CheckPermission();
  const { values, setFieldValue } = useFormikContext();
  const { t, i18n } = useTranslation();
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const hasInsuranceItem = values?.items?.some(
    (it) => it?.item_category === "insurance",
  );
  const hasApprovalItem = values?.items?.some((it) => it?.is_approval === true);
  const requiresApproval = hasApprovalItem;
  const showSessionPrice = values?.items?.some(
    (item) => Number(item?.number_of_sessions) > 1,
  );

  const handleCostSave = (totalManageCosts) => {
    if (selectedItemIndex === null) return;

    const numberOfSessions =
      values?.items[selectedItemIndex]?.number_of_sessions || 1;
    const unitPrice =
      numberOfSessions > 1
        ? (totalManageCosts / numberOfSessions).toFixed(2)
        : totalManageCosts;

    setFieldValue(
      `items[${selectedItemIndex}].original_price`,
      totalManageCosts,
    );
    setFieldValue(`items[${selectedItemIndex}].unit_price`, unitPrice);

    if (values?.items[selectedItemIndex]?.is_approval) {
      setFieldValue(`items[${selectedItemIndex}].approved_amount`, unitPrice);
    }
  };

  const { user, discountSettings } = useSelector((state) => state.auth);
  const departmentLimits = (values?.items || []).map(item =>
    getDepartmentDiscountLimit(user, item?.department_id, discountSettings)
  );
  const maxDiscount = departmentLimits.length
    ? Math.min(...departmentLimits)
    : getDepartmentDiscountLimit(user, null, discountSettings);
  const isBuisness42Receptionist = isBusiness42Receptionist(user);

  const getMaxFixedDiscount = (item) => {
    if (!isBuisness42Receptionist) return undefined;
    const sessions = Number(item?.number_of_sessions);
    const unitPrice = Number(item?.unit_price);
    const baseAmount =
      sessions > 1 ? unitPrice : Number(item?.original_price) || unitPrice;
    const departmentLimit = getDepartmentDiscountLimit(
      user,
      item?.department_id || billingVisitData?.practitioner?.department?.id,
      discountSettings
    );
    return Math.round(baseAmount * (departmentLimit / 100) * 100) / 100;
  };

  const columnCount = [
    isAddCheckBox,
    true,
    true,
    true,
    true,
    true,
    showSessionPrice,
    true,
    values?.patientType != "cash",
    true,
    isAccountant || isAdmin || isManager,
    true,
    requiresApproval,
    requiresApproval,
    requiresApproval,
    values?.items?.some(dt => dt?.fromVisit)
  ].filter(Boolean).length

  const renderItemRow = (item, index) => (
                  <tr
                    key={index}
                    className={`cursor-pointer border-b ${item?.item_category === "insurance" &&
                        item?.approval_status === "approved"
                        ? "bg-green-50"
                        : item?.item_category === "insurance" &&
                          item?.approval_status === "rejected"
                          ? "bg-red-50"
                          : "bg-white"
                      }`}
                  >
                    {isAddCheckBox && (
                      <td className="ps-2">
                        <input
                          disabled={
                            (item?.approval_status == "pending" ||
                              item?.approval_status == "rejected") &&
                            item?.item_category === "insurance" &&
                            item?.is_approval
                          }
                          type="checkbox"
                          name={`items[${index}].checked`}
                          className="bg-gray-100 border border-gray-200 h-[18px] rounded-lg w-[18px] mt-2"
                          checked={item?.checked}
                          onChange={(e) =>
                            setFieldValue(
                              `items[${index}].checked`,
                              e?.target?.checked,
                            )
                          }
                        />
                      </td>
                    )}
                    <td className="text-14-gray !font-semibold ps-3">
                      {item?.non_standard_code || item?.standard_code}
                    </td>
                    <td className="text-14-gray  !font-semibold">
                      {i18n.language === "en"
                        ? item?.non_standard_description
                        : item?.non_standard_description_ar || item?.item_name}
                    </td>
                    <td className="text-14-gray  !font-semibold">
                      {item?.item_category || "-"}
                    </td>
                    <td className="text-14-gray  !font-semibold">
                      {item?.number_of_sessions || "1"}
                    </td>
                    <td>
                      <TextInput
                        readOnly={
                          isAdmin || isAccountant || isManager
                            ? false
                            : !item?.order_changeable_price
                        }
                        type="number"
                        step="any"
                        name={`items[${index}].original_price`}
                        hideErrorText={true}
                        onChange={(e) => {
                          const value =
                            e.target.value === "" ? "" : Number(e.target.value);
                          setFieldValue(
                            `items[${index}].original_price`,
                            value,
                          );
                          const sessions = item?.number_of_sessions || 0;
                          const calculatedUnitPrice = (
                            sessions ? value / sessions : value || 0
                          ).toFixed(2);
                          setFieldValue(
                            `items[${index}].unit_price`,
                            calculatedUnitPrice,
                          );
                          setFieldValue(
                            `items[${index}].approved_amount`,
                            calculatedUnitPrice,
                          );
                        }}
                        className="!w-20 !text-lg"
                                min={0}
                      />
                    </td>
                    {showSessionPrice && (
                      <td>
                        {Number(item?.number_of_sessions) > 1 && (
                          <TextInput
                            readOnly={!isAdmin && !isAccountant && !isManager}
                            type="number"
                            step="any"
                            name={`items[${index}].unit_price`}
                            hideErrorText={true}
                            className="!w-20 !text-lg"
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? ""
                                  : Number(e.target.value);
                              setFieldValue(
                                `items[${index}].unit_price`,
                                value,
                              );
                              if (item?.is_approval == true) {
                                setFieldValue(
                                  `items[${index}].approved_amount`,
                                  value,
                                );
                              }
                            }}
                            min={0}
                          />
                        )}
                      </td>
                    )}
                    <td>
                      <TextInput
                        name={`items[${index}].quantity`}
                        className="!w-20 !text-lg"
                        hideErrorText={true}
                        onChange={(e) => {
                          const value =
                            e.target.value === "" ? "" : Number(e.target.value);
                          if (isQuantityOverStock(item, value)) {
                            showStockLimitToast(toast, item?.stock);
                            return;
                          }
                          setFieldValue(`items[${index}].quantity`, value);
                          setFieldValue(`items[${index}].approved_qty`, value);
                        }}
                        type="number"
                        step="any"
                        min={0}
                      />
                    </td>
                    {values?.patientType !== "cash" &&
                      item?.item_category != "cash" ? (
                      <td>
                        <TextInput
                          readOnly={!isAdmin && !isAccountant && !isManager}
                          name={`items[${index}].patient_share`}
                          className="!w-20 !text-lg"
                          hideErrorText={true}
                          type="number"
                          step="any"
                          min={0}
                        />
                      </td>
                    ) : values?.patientType != "cash" ? (
                      <td>
                        <TextInput
                          readOnly={!isAdmin && !isAccountant && !isManager}
                          useFormik={false}
                          value={100}
                          className="!w-20"
                          type="number"
                          step="any"
                          min={0}
                        />
                      </td>
                    ) : null}
                    <td>
                      <div className="flex gap-x-2 items-center">
                        <DropdownField
                          disabled={!isAlowActions}
                          name={`items[${index}].discount_type`}
                          value={item?.discount_type || "percentage"}
                          options={visitDiscountOptions(t)}
                          optionLabel="label"
                          placeholder={t("Selectdiscounttype")}
                          parentClass="!w-fit"
                          hideErrorText={true}
                          onChange={(e) => {
                            const newType = e.value;
                            setFieldValue(
                              `items[${index}].discount_type`,
                              newType,
                            );
                            const currentAmount =
                              Number(item?.discount_amount) || 0;
                            const maxValue =
                              newType === "percentage"
                                ? getDepartmentDiscountLimit(user, item?.department_id || billingVisitData?.practitioner?.department?.id, discountSettings)
                                : getMaxFixedDiscount(item) ??
                                  Number.MAX_SAFE_INTEGER;
                            setFieldValue(
                              `items[${index}].discount_amount`,
                              Math.min(currentAmount, maxValue),
                            );
                          }}
                        />
                        <TextInput
                          readOnly={
                            !isAlowActions ||
                            (!isAdmin && !isReceptionist && !isManager)
                          }
                          type="number"
                          step="any"
                                name={`items[${index}].discount_amount`}
                                className="!w-20 !text-lg "
                                hideErrorText={true}
                                min={0}
                                max={
                                  showGroupHeaders && item?.checked !== true
                                    ? undefined
                                    : item?.discount_type === "percentage"
                                      ? getDepartmentDiscountLimit(user, item?.department_id || billingVisitData?.practitioner?.department?.id, discountSettings)
                                      : getMaxFixedDiscount(item)
                                }
                                onChange={(event) => {
                                  const enteredValue = Number(event.target.value) || 0
                                  const maxValue =
                                    item?.discount_type === "percentage"
                                      ? getDepartmentDiscountLimit(user, item?.department_id || billingVisitData?.practitioner?.department?.id, discountSettings)
                                      : getMaxFixedDiscount(item) ?? Number.MAX_SAFE_INTEGER
                                  const value = Math.min(enteredValue, maxValue)
                                  setFieldValue(`items[${index}].discount_amount`, value)
                                }}
                        />
                      </div>
                    </td>
                    {(isAccountant || isAdmin || isManager) && (
                      <td>
                        <SwitchInput
                          // disabled
                          parentClass="mt-3"
                          value={values?.items[index]?.tax_check}
                          onChange={(event) => {
                            setFieldValue(
                              `items[${index}].tax_check`,
                              event.target.value,
                            );
                          }}
                        />
                      </td>
                    )}
                    <td>
                      <SwitchInput
                        disabled
                        parentClass="mt-3"
                        value={values?.items[index]?.is_approval}
                        onChange={(event) => {
                          setFieldValue(
                            `items[${index}].is_approval`,
                            event.target.value,
                          );
                        }}
                      />
                    </td>
                    {item?.is_approval ? (
                      <>
                        <td>
                          <TextInput
                            readOnly
                            type="number"
                            step="any"
                            name={`items[${index}].approved_qty`}
                            className="!w-20 !text-lg"
                            hideErrorText={true}
                            min={0}
                          />
                        </td>
                        <td>
                          <TextInput
                            readOnly
                            type="number"
                            step="any"
                            name={`items[${index}].approved_amount`}
                            className="!w-20 !text-lg"
                            hideErrorText={true}
                            min={0}
                          />
                        </td>
                        <td>
                          <DropdownField
                            disabled
                            value={item?.approval_status}
                            options={approvalStatusOptions(t)}
                            placeholder={t("Select Status")}
                            name={`items[${index}].approval_status`}
                            className="!w-fit"
                            hideErrorText={true}
                          />
                        </td>
                      </>
                    ) : (
                      values?.items?.find((item) => item.is_approval) && (
                        <>
                          <td></td>
                          <td></td>
                          <td></td>
                        </>
                      )
                    )}
                    {isAlowActions && (
                      <td>
                        <p className="flex justify-center items-center">
                          {item?.manage_costs?.length > 0 && (
                            <FaCalculator
                              size={15}
                              className="text-blue mx-1 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItemIndex(index);
                                setCostModalOpen(true);
                              }}
                              title={t("Cost Management")}
                            />
                          )}
                          {item?.fromVisit ? (
                              <DeleteIcon
                                disabled
                                iconSize="20"
                                onClick={() => {
                                  const updatedItems = (
                                    billingVisitData?.items || []
                                  ).map((itm) => {
                                    const editedItem = values?.items?.find(
                                      (vItem) =>
                                        vItem.item_id === itm.item_id &&
                                        itm?.status?.toLowerCase() === "unpaid" &&
                                        vItem.item_category === itm.item_category,
                                    );
                                    return editedItem
                                      ? { ...itm, ...editedItem }
                                      : itm;
                                  });
                                  const filteredItems = updatedItems?.filter(
                                    (itm) =>
                                      itm?.item_id !== item?.item_id ||
                                      itm?.status?.toLowerCase() !== "unpaid" ||
                                      itm?.item_category !== item?.item_category,
                                  );
                                  setBillingVisitData((prev) => ({
                                    ...prev,
                                    items: filteredItems,
                                  }));
                                }}
                              />
                          ) : null}
                        </p>
                      </td>
                    )}
                  </tr>
  )

  const setSectionChecked = (entries, checked) => {
    const nextItems = [...(values?.items || [])];
    entries.forEach(({ index }) => {
      const item = nextItems[index];
      if (item && !isApprovalPendingItem(item)) {
        nextItems[index] = { ...item, checked };
      }
    });
    setFieldValue("items", nextItems);
  };

  const renderSectionHeaderRow = (key, label, entries, isSubSection) => {
    const sectionItems = entries.map(entry => entry.item);
    const state = getUnitSelectionState(sectionItems, item => item?.checked);
    const hasToggleableItem = sectionItems.some(
      item => !isApprovalPendingItem(item)
    );

    return (
      <tr key={key}>
        <td
          colSpan={columnCount}
          className={
            isSubSection
              ? "bg-gray-200"
              : "bg-gray-300 border-y-2 border-gray-400"
          }
        >
          <div
            className={`flex items-center justify-between gap-3 ps-2 pe-4 ${
              isSubSection ? "py-2" : "py-2.5"
            }`}
          >
            <div className="flex items-center gap-3">
              <TriStateCheckbox
                state={state}
                disabled={!hasToggleableItem}
                onChange={() => setSectionChecked(entries, state !== "all")}
              />
              <span
                className={
                  isSubSection
                    ? "ps-6 text-[13px] font-semibold text-gray-700"
                    : "text-[15px] font-bold uppercase tracking-wide text-gray-900"
                }
              >
                {label}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {!isSubSection && (
                <span className="text-[11px] font-medium uppercase tracking-wide text-gray-600">
                  {t("Patient Payable")}
                </span>
              )}
              <div className="flex w-[112px] items-baseline justify-end gap-1">
                <span
                  className={`tabular-nums ${
                    isSubSection
                      ? "text-[13px] font-semibold text-gray-700"
                      : "text-[15px] font-bold text-gray-900"
                  }`}
                >
                  {Number(
                    getGroupTotal?.(sectionItems, values) || 0
                  ).toFixed(2)}
                </span>
                <span
                  className={`text-[10px] font-medium uppercase ${
                    isSubSection ? "text-gray-500" : "text-gray-600"
                  }`}
                >
                  SAR
                </span>
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const renderGroupedRows = () => {
    const items = values?.items || [];
    const categories = [];

    items.forEach((item, index) => {
      const key = getBillTabKey(item);
      let category = categories.find(entry => entry.key === key);
      if (!category) {
        category = { key, entries: [] };
        categories.push(category);
      }
      category.entries.push({ item, index });
    });

    const rows = [];
    categories.forEach(category => {
      const baseLabel =
        priceListType?.find(type => type?.value === category.key)?.label ||
        category.key;

      rows.push(
        renderSectionHeaderRow(
          `category-${category.key}`,
          `${baseLabel} (${category.entries.length})`,
          category.entries,
          false
        )
      );

      const cashEntries = category.entries.filter(
        entry => entry.item?.item_category === "cash"
      );
      const insuranceEntries = category.entries.filter(
        entry => entry.item?.item_category !== "cash"
      );

      if (cashEntries.length && insuranceEntries.length) {
        rows.push(
          renderSectionHeaderRow(
            `category-${category.key}-cash`,
            `${t("Cash Items")} (${cashEntries.length})`,
            cashEntries,
            true
          )
        );
        cashEntries.forEach(entry =>
          rows.push(renderItemRow(entry.item, entry.index))
        );

        rows.push(
          renderSectionHeaderRow(
            `category-${category.key}-insurance`,
            `${t("Insurance Items")} (${insuranceEntries.length})`,
            insuranceEntries,
            true
          )
        );
        insuranceEntries.forEach(entry =>
          rows.push(renderItemRow(entry.item, entry.index))
        );
      } else {
        category.entries.forEach(entry =>
          rows.push(renderItemRow(entry.item, entry.index))
        );
      }
    });

    return rows;
  };

  const renderSkeleton = () => (
    <div className="w-full border border-gray-200 mt-2 animate-pulse">
      {[0, 1].map(section => (
        <div key={section}>
          <div className="flex items-center gap-3 border-y-2 border-gray-400 bg-gray-300 px-3 py-2.5">
            <div className="h-[18px] w-[18px] rounded bg-gray-400" />
            <div className="h-4 w-40 rounded bg-gray-400" />
            <div className="ms-auto h-4 w-24 rounded bg-gray-400" />
          </div>
          <div className="flex items-center gap-3 bg-gray-200 ps-10 pe-4 py-2">
            <div className="h-[18px] w-[18px] rounded bg-gray-400" />
            <div className="h-3.5 w-28 rounded bg-gray-400" />
          </div>
          {[0, 1, 2].map(row => (
            <div
              key={row}
              className="flex items-center gap-4 border-b border-gray-100 px-3 py-2.5"
            >
              <div className="h-[18px] w-[18px] rounded bg-gray-200" />
              <div className="h-3.5 w-20 rounded bg-gray-200" />
              <div className="h-3.5 w-48 rounded bg-gray-200" />
              <div className="h-3.5 w-16 rounded bg-gray-200" />
              <div className="ms-auto h-3.5 w-24 rounded bg-gray-200" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const renderTable = (
    <div>
      {showGroupHeaders && itemsLoading ? (
        renderSkeleton()
      ) : values?.items?.length ? (
        <div className="w-full border border-gray-200 mt-2 overflow-x-auto">
          <table className="w-full">
            <thead className="rounded !px-4 sticky top-0 z-10">
              <tr>
                {isAddCheckBox && (
                  <th className={`table-header !font-bold`}></th>
                )}
                <th className={`table-header !font-bold ps-3`}> {t("Code")}</th>
                <th className={`table-header !font-bold min-`}> {t("Name")}</th>
                <th className={`table-header !font-bold `}> {t("Type")}</th>
                <th className={`table-header !font-bold `}> {t("Sessions")}</th>
                <th className={`table-header !font-bold`}> {t("U.Price")}</th>
                {showSessionPrice && (
                  <th className={`table-header !font-bold`}> {t("S.Price")}</th>
                )}
                <th className={`table-header !font-bold`}> {t("Qty")}</th>
                {values?.patientType != "cash" ? (
                  <th className={`table-header !font-bold`}>{t("P.Share")}</th>
                ) : null}
                <th className={`table-header !font-bold`}>{t("Disc")}</th>
                {(isAccountant || isAdmin || isManager) && (
                  <th className={`table-header !font-bold`}>{t("Vat")}</th>
                )}
                <th className={`table-header !font-bold `}> {t("Apprv.")}</th>

                {requiresApproval ? (
                  <>
                    <th className={`table-header !font-bold `}>
                      {" "}
                      {t("Apprv Qty")}
                    </th>
                    <th className={`table-header !font-bold `}>
                      {" "}
                      {t("Apprv Amnt")}
                    </th>
                    <th className={`table-header !font-bold`}>
                      {" "}
                      {t("Apprv Status")}
                    </th>
                  </>
                ) : null}
                {values?.items?.some((dt) => dt?.fromVisit) ? (
                  <th className={`table-header !font-bold `}>{t("Actions")}</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {showGroupHeaders
                ? renderGroupedRows()
                : values?.items?.map((item, index) => renderItemRow(item, index))}
            </tbody>
          </table>
        </div>
      ) : (
        ""
      )}
    </div>
  );

  return (
    <>
      {renderTable}
      <CostManagementModal
        isVisible={costModalOpen}
        onClose={() => setCostModalOpen(false)}
        selectedItemIndex={selectedItemIndex}
        setFieldValue={setFieldValue}
        formValues={values}
        onSave={handleCostSave}
      />
    </>
  );
};

export default React.memo(VisitPriceItemsPayBill);
